import { DialogController, DialogService } from 'aurelia-dialog';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject, bindable, PLATFORM } from 'aurelia-framework';
import { NavigationInstruction, Next, Redirect, Router, RouterConfiguration } from 'aurelia-router';
import { Prompt } from 'elements/prompt';
import { Models } from 'models/core';
import { ErrorService } from 'services/error-service';
import { LoopService } from 'services/loop-service';
import { NetService } from 'services/net-service';
import { AnalysisService } from 'services/analysis-service';
import { AnalysisRopeService } from 'services/analysis-rope-service';
import { ToastService } from 'services/toast-service';
import { Role } from 'models/UserModels';

@autoinject
export class FormRope {
  private analysisId: number;
  private analysis: Models.Analysis;
  private ropes: Array<Models.AnalysisRope>;
  private ropeNewFormVisible: boolean = false;
  private ropeEditFormVisible: number = null;
  private minimumBreakingStrength: any;
  private nextTabIndex: number = null;
  private locked: boolean = false;
  private validationMessages: Array<string>;

  private subscription1: Subscription;
  private subscription2: Subscription;
  private subscription3: Subscription;
  private subscription4: Subscription;

  rolesAllowedToUpdate: Role[] = ['AnalysisEngineer'];

  constructor(
    private dialogService: DialogService,
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private loopService: LoopService,
    private netService: NetService,
    private analysisService: AnalysisService,
    private analysisRopeService: AnalysisRopeService,
    private router: Router,
    private toastService: ToastService
  ) {}

  private activate(params) {
    this.analysisId = parseInt(params.Id);
    this.getRopes(this.analysisId);
    //this.getLoops(this.netId);

    // Update ropes array when creating new ropes
    this.subscription1 = this.eventAggregator.subscribe('ropeListReset', (value) => {
      this.ropeEditFormVisible = null;
      this.getRopes(this.analysisId);
    });

    this.subscription3 = this.eventAggregator.subscribe('ropeFormEditClose', (value) => {
      return this.getRopes(this.analysisId);
    });

    this.eventAggregator.subscribe('ropeFormNewClose', (value) => {
      this.ropeNewFormVisible = false;
    });

    // Get new tabIndex for tabs component via EA, store value so we can publish this if canDeactivate returns true
    this.eventAggregator.subscribe('changeTab', (tabIndex) => {
      this.nextTabIndex = tabIndex;
    });
  }

  private deactivate() {
    if (this.subscription1) {
      this.subscription1.dispose();
    }
    if (this.subscription2) {
      this.subscription2.dispose();
    }
    if (this.subscription3) {
      this.subscription3.dispose();
    }
    if (this.subscription4) {
      this.subscription4.dispose();
    }
  }

  private getAnalysis() {
    this.analysisService
      .get(this.analysisId)
      .then((res) => {
        this.analysis = res;
        this.locked = res.Locked ? res.Locked : false;
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private canDeactivate() {
    if (this.ropeNewFormVisible || this.ropeEditFormVisible > 0) {
      return this.dialogService
        .open({
          viewModel: Prompt,
          model: {
            header: 'dialog.subFormOpenHeading',
            message: 'dialog.subFormOpenMessage',
            actions: {
              delete: { enabled: false },
              save: { enabled: false },
              cancel: { enabled: true, t: 'dialog.cancel' },
              dontsave: { enabled: false },
              continue: { enabled: true, t: 'dialog.continue' },
            },
          },
        })
        .whenClosed((response) => {
          if (response.wasCancelled) {
            return false;
          } else {
            const result = response.output;
            if (result === 'continue') {
              this.eventAggregator.publish('changeTab-success', this.nextTabIndex);
              return true;
            }
          }
        });
    } else {
      this.eventAggregator.publish('changeTab-success', this.nextTabIndex);
      return true;
    }
  }

  private getRopes(analysisId) {
    this.analysisRopeService
      .getAll('?$expand=RopePlacement,RopeDimension,RopeType,Material&$filter=AnalysisId eq ' + analysisId)
      .then((res) => {
        this.ropes = res;
        this.getAnalysis();
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private deleteRope(id) {
    this.analysisRopeService
      .delete(id)
      .then((res) => {
        this.toastService.showSuccess('rope.deleted');
        this.getRopes(this.analysisId);
      })
      .catch((err) => this.errorService.handleError(err));
  }
}
